import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { components } from '@/api/qraft/schema';
import { ModalFooter, ModalHeader, ModalTitle } from '@/common/Modal.styles';
import { ModalCloseButton } from '@/common/ModalCloseButton';
import { WBSButton } from '@/common/WBSButton';
import { AdoptModalBody } from './AdoptModalBody';
import {
  ANIMATION_DURATION,
  AnimatedModalContent,
  AnimatedOverlay,
  RequestModalBody,
} from './StudyHelperRequestModal.styles';
import { SwitchModalBody } from './SwitchModalBody';

type StudyHelperBoardItem = components['schemas']['StudyHelperBoardItem'];
type SwitchReason = components['schemas']['SwitchReason'];

interface StudyHelperRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    reason: SwitchReason | null,
    reasonOther: string | null,
  ) => Promise<void>;
  onSubmitted: () => void;
  variant: 'adopt' | 'switch';
  requestedHelper: StudyHelperBoardItem | null;
  currentHelper?: StudyHelperBoardItem;
}

export function StudyHelperRequestModal({
  isOpen,
  onClose,
  onSubmit,
  onSubmitted,
  variant,
  requestedHelper,
  currentHelper,
}: StudyHelperRequestModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<SwitchReason | ''>('');
  const [reasonError, setReasonError] = useState(false);
  const [reasonOther, setReasonOther] = useState('');
  const [reasonOtherError, setReasonOtherError] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // When parent closes externally (e.g. navigation), trigger exit animation
  useEffect(() => {
    if (!isOpen && !isClosing) {
      setIsClosing(false);
    }
  }, [isOpen, isClosing]);

  if ((!isOpen && !isClosing) || !requestedHelper) return null;

  const resetForm = () => {
    setReason('');
    setReasonError(false);
    setReasonOther('');
    setReasonOtherError(false);
  };

  const triggerClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      resetForm();
      onClose();
    }, ANIMATION_DURATION);
  };

  const handleSubmit = async () => {
    if (variant === 'switch') {
      // A reason is always required, and the Other text only when the reason is
      // Other. Both are checked together so the banner reports either.
      const missingReason = !reason;
      const missingOther = reason === 'other' && !reasonOther.trim();
      if (missingReason || missingOther) {
        setReasonError(missingReason);
        setReasonOtherError(missingOther);
        return;
      }
    }
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(
        variant === 'switch' && reason ? reason : null,
        variant === 'switch' && reason === 'other' ? reasonOther.trim() : null,
      );
    } catch {
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);

    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      resetForm();
      onSubmitted();
    }, ANIMATION_DURATION);
  };

  const handleClose = () => {
    triggerClose();
  };

  return (
    <AnimatedOverlay $closing={isClosing} onClick={handleClose}>
      <AnimatedModalContent
        $closing={isClosing}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            {variant === 'adopt'
              ? t('wbs.request-to-be-adopted')
              : t('wbs.request-to-switch-study-helper')}
          </ModalTitle>
          <ModalCloseButton onClick={handleClose} />
        </ModalHeader>

        <RequestModalBody>
          {variant === 'adopt' ? (
            <AdoptModalBody requestedHelper={requestedHelper} />
          ) : (
            <SwitchModalBody
              requestedHelper={requestedHelper}
              currentHelper={currentHelper}
              reason={reason}
              reasonError={reasonError}
              onReasonChange={(val) => {
                setReason(val);
                if (val) setReasonError(false);
                // Leaving Other hides its field, so clear its error too, or the
                // banner sticks on a hidden control.
                if (val !== 'other') setReasonOtherError(false);
              }}
              reasonOther={reasonOther}
              reasonOtherError={reasonOtherError}
              onReasonOtherChange={(val) => {
                setReasonOther(val);
                if (val.trim()) setReasonOtherError(false);
              }}
            />
          )}
        </RequestModalBody>

        <ModalFooter>
          <WBSButton onClick={handleSubmit} wide isLoading={isSubmitting}>
            {t('wbs.send-request')}
          </WBSButton>
        </ModalFooter>
      </AnimatedModalContent>
    </AnimatedOverlay>
  );
}
